import { useCallback, useEffect, useMemo } from 'react'
import { appUiStateFactory } from '~m/AppUi/context/context.ts'
import { getPublicConfig } from '~/config.ts'
import { useAuthUser } from '@soniq/util-auth'
import { ResourceRequestOptions } from '@soniq/public-resource'

/**
 * Request options for domains that only talk to the API over
 * HTTP, so they carry no local db.
 */
export type BasicResourceOptions = Pick<
  ResourceRequestOptions,
  'apiBaseUrl' | 'accessToken'
>

/**
 * Request options that may be missing the access token, for
 * reads that work signed out as well as signed in, such as
 * viewing a shared patch.
 */
export function useBasicResourceOptions(): BasicResourceOptions {
  const { apiBaseUrl } = getPublicConfig()
  const accessToken = useAuthUser()?.access_token
  // Memoised so hooks that list the options as a dependency
  // (fetches, save callbacks) are not rebuilt on every render
  return useMemo(
    () => ({ apiBaseUrl, accessToken }),
    [apiBaseUrl, accessToken]
  )
}

export const useSetAppUiState =
  appUiStateFactory.useSetAppUiState

export const useScreenTitle = appUiStateFactory.useScreenTitle

export function useSetScreenTitle(title: string | null) {
  const setAppUiState = useSetAppUiState()
  useEffect(() => {
    setAppUiState({ screenTitle: title })
    return () => setAppUiState({ screenTitle: title })
  }, [setAppUiState, title])
}

export function useGlobalDialog() {
  const setAppUiState = useSetAppUiState()
  const globalDialog = appUiStateFactory.useGlobalDialog()
  return {
    isQuickSearchOpen: globalDialog === 'search',
    globalDialog,
    showGlobalDialog: (dialog: typeof globalDialog) => {
      setAppUiState({ globalDialog: dialog })
    },
  }
}

export function useActionsDrawer() {
  const setAppUiState = useSetAppUiState()
  const isOpen = appUiStateFactory.useActionsDrawerOpen()
  const setActionsDrawerOpen = useCallback(
    (open: boolean) => {
      setAppUiState({ actionsDrawerOpen: open })
    },
    [setAppUiState]
  )
  const openActionsDrawer = useCallback(
    () => setAppUiState({ actionsDrawerOpen: true }),
    [setAppUiState]
  )
  const closeActionsDrawer = useCallback(
    () => setAppUiState({ actionsDrawerOpen: false }),
    [setAppUiState]
  )
  return {
    isOpen,
    setActionsDrawerOpen,
    openActionsDrawer,
    closeActionsDrawer,
  }
}
